/**
 * Two or three options, no gap, one frame — the boards' theme switch.
 *
 * It is not a Chip row and not a Tabs bar: a segmented control says "exactly one of these, and the
 * set is small and fixed", which is what makes it right for choosing a look or a wall and wrong
 * for anything that could grow a fourth option later.
 *
 * The drawing and the behaviour moved to `ui/toggle-group.tsx`, which is RNR's Toggle Group on
 * `@rn-primitives/toggle-group` at `type="single"` — so the roving state, the roles and the
 * "always one on" rule come from the primitive rather than from a `Pressable` row. This is the
 * app's own calling convention over it: a typed `options` array and `value`/`onChange`, which is
 * what the four call sites already pass.
 *
 * The selected option fills with `bg-seg-on` and takes a 2px RING — Bouquet's in `gold-edge`,
 * Tuxedo's in `line-strong` — because at night Bouquet's two fills are only 1.32:1 apart and the
 * ring is what says which one is on. That is the one thing the two rooms disagree about here, so
 * it is the one thing read out of the store.
 *
 * A design-system component: catalogued as `Segmented` (extended, over RNR's Toggle Group) in
 * `LaunchPad/ideas/_design.md`, which is the name the roster's `Toggle Group / as: Segmented`
 * line points at.
 */
import { Text } from '@/components/ui/text';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { useStore } from '@/store';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  accessibilityLabel,
  className,
}: {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  accessibilityLabel?: string;
  className?: string;
}) {
  const look = useStore((s) => s.look);
  const ring = look === 'tuxedo' ? 'border-line-strong' : 'border-gold-edge';

  return (
    <ToggleGroup
      type="single"
      value={value}
      /**
       * The primitive hands back `undefined` for a press that would deselect, and `ToggleGroupItem`
       * already swallows that press — so this only ever sees a real value. The guard is here
       * anyway, because a control that can hold nothing is not the control this is.
       */
      onValueChange={(next) => {
        if (next) onChange(next as T);
      }}
      accessibilityLabel={accessibilityLabel}
      className={className}>
      {options.map((option, i) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          ring={ring}
          accessibilityLabel={option.label}
          /**
           * The selected option takes the frame's radius less its border on whichever outer corner
           * it owns. Without it the container's `overflow-hidden` slices the square corners off the
           * ring and the chip reads as a rectangle with its corners shaved — seen at 2.6x on the
           * board's first shot of this control.
           */
          className={cn(
            i === 0 && 'rounded-l-[7px]',
            i === options.length - 1 && 'rounded-r-[7px]',
          )}>
          <Text>{option.label}</Text>
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
